import type { 
  Encounter, 
  Prescription, 
  Order
} from '../types';

class MockConsultationDB {
  private storageKey = 'consultation_data';

  private data = {
    encounters: [] as Encounter[],
    prescriptions: [] as Prescription[],
    orders: [] as Order[],
  };

  constructor() {
    this.loadFromStorage();
    this.seedInitialData();
  }

  private loadFromStorage() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        this.data = { ...this.data, ...JSON.parse(stored) };
      }
    } catch (error) {
      console.warn('Failed to load consultation data from localStorage:', error);
    }
  }

  private saveToStorage() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.data));
    } catch (error) {
      console.warn('Failed to save consultation data to localStorage:', error);
    }
  }

  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private seedInitialData() {
    const today = new Date().toISOString().split('T')[0];
    
    const encounters: Encounter[] = [
      {
        id: 'enc-1',
        patientId: 'patient-1',
        doctorId: 'doctor-1',
        appointmentId: 'appointment-1',
        dateTime: `${today}T09:00:00.000Z`,
        vitals: {
          bp: '120/80',
          hr: '72',
          temp: '98.6',
          spo2: '98',
          bmi: '24.5',
        },
        complaints: 'Fever and headache for 2 days',
        notes: 'Patient appears well. No acute distress.',
        diagnosis: [
          { code: 'J00', label: 'Acute nasopharyngitis (common cold)' }
        ],
        status: 'final',
        createdAt: `${today}T09:00:00.000Z`,
        updatedAt: `${today}T09:00:00.000Z`,
      },
      {
        id: 'enc-2',
        patientId: 'patient-2',
        doctorId: 'doctor-2',
        appointmentId: 'appointment-2',
        dateTime: `${today}T11:30:00.000Z`,
        vitals: {
          bp: '130/85',
          hr: '78',
          temp: '99.1',
          spo2: '97',
          bmi: '26.2',
        },
        complaints: 'Cough and chest congestion',
        notes: 'Mild respiratory symptoms. Chest clear on auscultation.',
        diagnosis: [
          { code: 'J20', label: 'Acute bronchitis' }
        ],
        status: 'final',
        createdAt: `${today}T11:30:00.000Z`,
        updatedAt: `${today}T11:30:00.000Z`,
      },
    ];

    const prescriptions: Prescription[] = [
      {
        id: 'rx-1',
        encounterId: 'enc-1',
        patientId: 'patient-1',
        items: [
          {
            nameOrMedicineId: 'Paracetamol',
            dose: '500mg',
            frequency: 'TID',
            duration: '5 days',
            route: 'oral',
            instructions: 'Take after meals',
          },
        ],
        signedBy: 'Dr. John Smith',
        createdAt: `${today}T09:15:00.000Z`,
        updatedAt: `${today}T09:15:00.000Z`,
      },
      {
        id: 'rx-2',
        encounterId: 'enc-2',
        patientId: 'patient-2',
        items: [
          {
            nameOrMedicineId: 'Amoxicillin',
            dose: '250mg',
            frequency: 'TID',
            duration: '7 days',
            route: 'oral',
            instructions: 'Take with food',
          },
        ],
        signedBy: 'Dr. Sarah Johnson',
        createdAt: `${today}T11:45:00.000Z`,
        updatedAt: `${today}T11:45:00.000Z`,
      },
    ];

    const orders: Order[] = [
      {
        id: 'order-1',
        encounterId: 'enc-1',
        type: 'lab',
        items: [
          { code: 'CBC', name: 'Complete Blood Count' }
        ],
        priority: 'normal',
        notes: 'Routine follow-up',
        status: 'pending',
        createdAt: `${today}T09:20:00.000Z`,
        updatedAt: `${today}T09:20:00.000Z`,
      },
    ];

    this.data.encounters = encounters;
    this.data.prescriptions = prescriptions;
    this.data.orders = orders;
    this.saveToStorage();
  }

  getEncounters(): Encounter[] {
    return [...this.data.encounters];
  }

  getEncounter(id: string): Encounter | undefined {
    return this.data.encounters.find(e => e.id === id);
  }

  createEncounter(data: Omit<Encounter, 'id' | 'createdAt' | 'updatedAt'>): Encounter {
    const now = new Date().toISOString();
    const encounter: Encounter = {
      ...data,
      id: this.generateId(),
      createdAt: now,
      updatedAt: now,
    };
    this.data.encounters.push(encounter);
    this.saveToStorage();
    return encounter;
  }

  updateEncounter(id: string, data: Partial<Omit<Encounter, 'id' | 'createdAt'>>): Encounter | null {
    const index = this.data.encounters.findIndex(e => e.id === id);
    if (index === -1) return null;

    this.data.encounters[index] = {
      ...this.data.encounters[index],
      ...data,
      updatedAt: new Date().toISOString(),
    };
    this.saveToStorage();
    return this.data.encounters[index];
  }

  getTodaysEncounters(): Encounter[] {
    const today = new Date().toISOString().split('T')[0];
    return this.data.encounters.filter(e => 
      e.dateTime.startsWith(today)
    );
  }

  getPrescriptions(): Prescription[] {
    return [...this.data.prescriptions];
  }

  getPrescription(id: string): Prescription | undefined {
    return this.data.prescriptions.find(p => p.id === id);
  }

  getPrescriptionByEncounter(encounterId: string): Prescription | undefined {
    return this.data.prescriptions.find(p => p.encounterId === encounterId);
  }

  createPrescription(data: Omit<Prescription, 'id' | 'createdAt' | 'updatedAt'>): Prescription {
    const now = new Date().toISOString();
    const prescription: Prescription = {
      ...data,
      id: this.generateId(),
      createdAt: now,
      updatedAt: now,
    };
    this.data.prescriptions.push(prescription);
    this.saveToStorage();
    return prescription;
  }

  updatePrescription(id: string, data: Partial<Omit<Prescription, 'id' | 'createdAt'>>): Prescription | null {
    const index = this.data.prescriptions.findIndex(p => p.id === id);
    if (index === -1) return null;

    this.data.prescriptions[index] = {
      ...this.data.prescriptions[index],
      ...data,
      updatedAt: new Date().toISOString(),
    };
    this.saveToStorage();
    return this.data.prescriptions[index];
  }

  getOrders(): Order[] {
    return [...this.data.orders];
  }

  getOrder(id: string): Order | undefined {
    return this.data.orders.find(o => o.id === id);
  }

  getOrdersByEncounter(encounterId: string): Order[] {
    return this.data.orders.filter(o => o.encounterId === encounterId);
  }

  createOrder(data: Omit<Order, 'id' | 'createdAt' | 'updatedAt'>): Order {
    const now = new Date().toISOString();
    const order: Order = {
      ...data,
      id: this.generateId(),
      createdAt: now,
      updatedAt: now,
    };
    this.data.orders.push(order);
    this.saveToStorage();
    return order;
  }

  updateOrder(id: string, data: Partial<Omit<Order, 'id' | 'createdAt'>>): Order | null {
    const index = this.data.orders.findIndex(o => o.id === id);
    if (index === -1) return null;

    this.data.orders[index] = {
      ...this.data.orders[index],
      ...data,
      updatedAt: new Date().toISOString(),
    };
    this.saveToStorage();
    return this.data.orders[index];
  }
}

export const mockConsultationDB = new MockConsultationDB();
